/**
 * SERVICIO DE AUTENTICACIÓN
 * 
 * Este módulo centraliza todas las operaciones de autenticación y autorización
 * para el frontend de la aplicación, implementando un sistema DUAL que maneja
 * tanto usuarios administrativos (Supabase) como candidatos temporales (Render API).
 * 
 * Características principales v4.0:
 * - Sistema dual de autenticación (Supabase + Render API)
 * - Autenticación diferenciada por tipo de usuario:
 *   * Admin, Empresa, Evaluador: Supabase con validación de roles
 *   * Candidatos: API Render con credenciales temporales
 * - Validación previa de roles antes del login
 * - Gestión inteligente de sesiones con localStorage
 * - Manejo robusto de errores con estructura de respuesta flexible
 * - Integración con tabla 'company' para datos empresariales
 * - Soporte completo para tabla 'temporary_credentials'
 * 
 * Flujo de autenticación v4.0:
 * 1. Detección automática del tipo de usuario por rol seleccionado
 * 2. Enrutamiento a sistema de autenticación correspondiente:
 *    - Roles admin/empresa/evaluador → Supabase REST API
 *    - Rol candidato → Render API con credenciales temporales
 * 3. Validación y almacenamiento seguro de sesión
 * 4. Redirección automática según permisos de usuario
 * 
 * Endpoints utilizados:
 * - Supabase: validate_user_role_before_login, auth/v1/token
 * - Render API: /api/v1/temp-credentials/authenticate
 * 
 * @author Diego Alejandro
 * @version 4.0
 * @since 2025-08
 */

// URLs base para los endpoints REST de Supabase
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const RENDER_API_URL = process.env.REACT_APP_API_BASE_URL_RENDER; // Importar RENDER_API_URL

// Configuración de Supabase desde variables de entorno
const SUPABASE_CONFIG = {
  url: SUPABASE_URL,
  apiKey: SUPABASE_ANON_KEY
};

// Mapeo de roles a IDs para el endpoint
const ROLE_ID_MAPPING = {
  'admin': 1,
  'empresa': 2,
  'evaluador': 3,
  'candidato': 4
};
// Configuración temporal para simulación de desarrollo
const DEV_SIMULATION = {
  enabled: true, // Cambiar a false cuando el backend esté listo
  mockDistribuidorUser: {
    id: 'mock-distribuidor-001',
    email: 'distribuidor.demo@cuadranteh.com',
    username: 'distribuidor.demo@cuadranteh.com',
    role: 'evaluador', // Rol interno para Distribuidor Autorizado
    companyData: {
      id: 'company-mock-001',
      name: 'Distribuidora Demo S.A.S',
      nit: '900123456-1',
      phone: '+57 300 123 4567',
      address: 'Calle 123 #45-67, Bogotá'
    },
    roleValidation: {
      success: true,
      message: 'Usuario simulado para desarrollo'
    }
  }
};

// Función auxiliar para simular login
const simulateDistribuidorLogin = async () => {
  console.log(' SIMULACIÓN ACTIVA - Distribuidor Autorizado');
  
  // Simular delay de red
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  const mockUser = DEV_SIMULATION.mockDistribuidorUser;
  const mockToken = 'mock-token-' + Date.now();
  
  // Guardar en localStorage como login real
  localStorage.setItem('access_token', mockToken);
  localStorage.setItem('user', JSON.stringify(mockUser));
  
  console.log(' Login simulado exitoso para:', mockUser.email);
  
  return {
    token: mockToken,
    user: mockUser,
    session: { user: mockUser }
  };
};
/**
 * Servicio de autenticación que maneja todas las operaciones relacionadas
 * con la autenticación y autorización de usuarios
 */
const authService = {
  /**
   * Valida si un usuario tiene permisos para un rol específico antes del login
   * 
   * @param {string} email - Email del usuario
   * @param {string} role - Rol seleccionado ('admin', 'empresa', 'evaluador', 'candidato')
   * @returns {Promise<Object>} Resultado de la validación
   * @throws {Error} Error si la validación falla
   * 
   * @example
   * const result = await authService.validateUserRole('user@email.com', 'admin');
   */
  async validateUserRole(email, role) {
    const roleId = ROLE_ID_MAPPING[role];

    if (!roleId) {
      throw new Error('Rol no válido');
    }

    // Debug: Verificar configuración
    console.log('Configuración Supabase:', {
      url: SUPABASE_CONFIG.url,
      hasApiKey: !!SUPABASE_CONFIG.apiKey,
      apiKeyPreview: SUPABASE_CONFIG.apiKey ? `${SUPABASE_CONFIG.apiKey.substring(0, 20)}...` : 'No encontrada'
    });

    try {
      const response = await fetch(`${SUPABASE_CONFIG.url}/rest/v1/rpc/validate_user_role_before_login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${SUPABASE_CONFIG.apiKey}`,
          'apikey': SUPABASE_CONFIG.apiKey
        },
        body: JSON.stringify({
          user_email: email,
          selected_role_id: roleId
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Error en validación de rol:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(`Error del servidor: ${response.status} - ${errorData}`);
      }

      const data = await response.json();
      console.log('Resultado de validación de rol:', data);

      return data;
    } catch (error) {
      console.error('Error validating user role:', error);
      throw new Error(`Error de validación: ${error.message}`);
    }
  },

  /**
   * Inicia sesión con sistema dual de autenticación v4.0
   * 
   * FUNCIONALIDAD DUAL v4.0:
   * - Candidatos (rol 'candidato'): Autenticación con API Render usando credenciales temporales
   *   * Endpoint: /api/v1/temp-credentials/authenticate
   *   * Campos: temp_username, temp_password
   *   * Validación contra tabla temporary_credentials
   * 
   * - Usuarios Administrativos (admin/empresa/evaluador): Autenticación con Supabase
   *   * Validación previa de roles con RPC validate_user_role_before_login
   *   * Autenticación JWT con Supabase auth/v1/token
   *   * Integración con tabla company para datos empresariales
   * 
   * MEJORAS v4.0:
   * - Manejo defensivo de respuestas de API con estructura flexible
   * - Logging detallado para debugging y monitoreo
   * - Gestión inteligente de tokens y sesión temporal para candidatos
   * - Soporte para diferentes estructuras de respuesta (data.user, data.id, etc.)
   * 
   * @param {Object} credentials - Credenciales del usuario
   * @param {string} credentials.username - Usuario/email (para admin) o username temporal (para candidatos)
   * @param {string} credentials.password - Contraseña (permanente para admin, temporal para candidatos)
   * @param {string} credentials.role - Rol: 'admin'|'empresa'|'evaluador'|'candidato'
   * @returns {Promise<Object>} Objeto con token, user y session
   * @throws {Object} Error con mensaje descriptivo según el tipo de fallo
   * 
   * @example
   * // Login administrativo
   * const adminResult = await authService.login({
   *   username: 'admin@empresa.com',
   *   password: 'password123',
   *   role: 'empresa'
   * });
   * 
   * @example
   * // Login de candidato
   * const candidateResult = await authService.login({
   *   username: 'temp_user_001',
   *   password: 'temp_pass_123',
   *   role: 'candidato'
   * });
   */
  async login(credentials) {
     // SIMULACIÓN TEMPORAL - SOLO PARA DESARROLLO
    if (DEV_SIMULATION.enabled && credentials.role === 'evaluador') {
      return await simulateDistribuidorLogin();
    }
    // Si el rol es 'candidato', se usa un flujo de autenticación diferente
    if (credentials.role === 'candidato') {
      try {
        console.log(`Autenticando candidato con el endpoint personalizado: ${credentials.username}`);
        const response = await fetch(`${RENDER_API_URL}/api/temp-credentials/authenticate`,
          
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              temp_username: credentials.username,
              temp_password: credentials.password,
            }),
          });

        const data = await response.json();

        if (!response.ok) {
          const errorMessage = data.message || data.detail || 'Error de autenticación para el candidato.';
          console.error('Error en la autenticación del candidato:', { status: response.status, data });
          throw new Error(errorMessage);
        }

        // Debug: Ver estructura de la respuesta
        console.log('Respuesta de autenticación de candidato:', data);

        // Almacenar tokens si están disponibles
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
        }
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }

        // Crear objeto user con manejo seguro de la respuesta
        const user = {
          id: data.candidate_info?.id_candidato || data.user?.id || data.id || data.candidate_id || data.id_candidato,
          email: data.candidate_info?.email || data.user?.email || data.email || credentials.username,
          username: credentials.username,
          role: credentials.role,
          // Campos adicionales que podrían venir de la API
          candidateId: data.candidate_info?.id_candidato || data.candidate_id || data.id_candidato,
          companyId: data.company_id || data.id_empresa,
          fullName: data.candidate_info?.nombre_completo,
          testSessionId: data.test_session_id
        };

        localStorage.setItem('user', JSON.stringify(user));

        console.log(`Login de candidato exitoso. Rol: ${credentials.role}, Usuario: ${credentials.username}`);

        return {
          token: data.access_token,
          user,
          session: data,
        };

      } catch (error) {
        console.error('Error en el proceso de login de candidato:', error);
        throw error.message ? error : { message: 'Error en el servidor' };
      }
    }

    // Flujo de login para los demás roles (admin, empresa, evaluador)
    try {
      // PASO 1: Validar rol antes del login
      console.log('Validando rol con el servidor...');
      const roleValidationResult = await this.validateUserRole(credentials.username, credentials.role);

      if (!roleValidationResult || roleValidationResult.success === false) {
        const errorMessage = roleValidationResult?.message || 'El usuario no tiene permisos para este rol';
        console.error('Validación de rol falló:', roleValidationResult);
        throw new Error(errorMessage);
      }

      // PASO 2: Si la validación del rol es exitosa, proceder con el login
      console.log('Rol validado correctamente, procediendo con login...');

      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          email: credentials.username,
          password: credentials.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Login error details:', {
          status: response.status,
          statusText: response.statusText,
          data: data
        });
        throw { message: data.error_description || data.message || 'Error en el login' };
      }

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);

      const companyResponse = await fetch(`${SUPABASE_URL}/rest/v1/company?auth_user_id=eq.${data.user.id}`, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${data.access_token}`
        }
      });

      let companyData = null;
      if (companyResponse.ok) {
        const companies = await companyResponse.json();
        companyData = companies.length > 0 ? companies[0] : null;
      }

      const user = {
        id: data.user.id,
        email: data.user.email,
        username: credentials.username,
        role: credentials.role, // Usar el rol de las credenciales, no de user_metadata ni por defecto 'company'
        companyData: companyData,
        roleValidation: roleValidationResult
      };

      localStorage.setItem('user', JSON.stringify(user));

      console.log(`Login exitoso. Rol: ${credentials.role}, Usuario: ${credentials.username}`);

      return {
        token: data.access_token,
        user,
        session: data
      };
    } catch (error) {
      console.error('Error en el proceso de login:', error);
      throw error.message ? error : { message: 'Error en el servidor' };
    }
  },

  /**
   * Cierra la sesión del usuario actual
   * 
   * Invalida el token en el servidor (si existe) y limpia todos los datos
   * de autenticación almacenados en localStorage. Garantiza que la sesión
   * se cierre completamente incluso si hay errores en el servidor.
   * 
   * @returns {Promise<void>}
   * 
   * @example
   * await authService.logout();
   * // Usuario desconectado y datos de sesión eliminados
   */
  async logout() {
    try {
      const token = localStorage.getItem('access_token');
      // Si es token simulado, solo limpiar localStorage
      if (token && token.includes('mock-token')) {
        console.log(' Cerrando sesión simulada');
      } else if (token) {
        // Cerrar sesión real con Supabase (tu código original)
        await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      // Limpiar todos los datos de autenticación del localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('token_type'); // Legacy - mantener para compatibilidad
      localStorage.removeItem('authToken'); // Legacy - mantener para compatibilidad
      localStorage.removeItem('refreshToken'); // Legacy - mantener para compatibilidad
    }
  },

  /**
   * Obtiene el usuario actual desde localStorage
   * 
   * Recupera la información del usuario almacenada en localStorage
   * y verifica si el token sigue siendo válido.
   * 
   * @returns {Object|null} Datos del usuario o null si no está logueado
   * 
   * @example
   * const currentUser = authService.getCurrentUser();
   */
  getCurrentUser() {
    try {
      const token = localStorage.getItem('access_token');
      const userStr = localStorage.getItem('user');

      if (!token || !userStr) {
        return null;
      }

      const user = JSON.parse(userStr);
      return user;
    } catch (error) {
      console.error('Error al obtener usuario actual:', error);
      return null;
    }
  },

  /**
   * Verifica si el usuario está autenticado
   * 
   * Comprueba si existe un token válido en localStorage
   * y si el usuario está definido.
   * 
   * @returns {boolean} True si está autenticado, false si no
   * 
   * @example
   * const isLoggedIn = authService.isAuthenticated();
   */
  isAuthenticated() {
    try {
      const token = localStorage.getItem('access_token');
      const user = this.getCurrentUser();

      return !!(token && user);
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      return false;
    }
  },

  /**
   * Verifica si el usuario tiene un rol específico
   * 
   * Comprueba si el usuario actual posee el rol especificado.
   * 
   * @param {string} role - El rol a verificar
   * @returns {boolean} True si tiene el rol, false si no
   * 
   * @example
   * const isAdmin = authService.hasRole('admin');
   */
  hasRole(role) {
    try {
      const user = this.getCurrentUser();
      if (!user || !user.role) {
        return false;
      }

      return user.role === role;
    } catch (error) {
      console.error('Error al verificar rol:', error);
      return false;
    }
  },

  /**
   * Verifica si el usuario tiene al menos uno de los roles especificados
   * 
   * Comprueba si el usuario actual posee cualquiera de los roles
   * proporcionados en el array.
   * 
   * @param {string[]} roles - Array de roles a verificar
   * @returns {boolean} True si tiene al menos uno de los roles, false si no
   * 
   * @example
   * const hasAccess = authService.hasAnyRole(['admin', 'manager']);
   */
  hasAnyRole(roles) {
    try {
      if (!Array.isArray(roles) || roles.length === 0) {
        return false;
      }

      const user = this.getCurrentUser();
      if (!user || !user.role) {
        return false;
      }

      return roles.includes(user.role);
    } catch (error) {
      console.error('Error al verificar roles:', error);
      return false;
    }
  },

  /**
   * Obtiene la configuración de Supabase para debugging
   * 
   * @returns {Object} Configuración de Supabase con información de debug
   */
  getSupabaseConfig() {
    return {
      url: SUPABASE_CONFIG.url,
      hasApiKey: !!SUPABASE_CONFIG.apiKey,
      apiKeyPreview: SUPABASE_CONFIG.apiKey ? `${SUPABASE_CONFIG.apiKey.substring(0, 20)}...` : 'No encontrada',
      roleMapping: ROLE_ID_MAPPING
    };
  }
};

export default authService;